import { useEffect, useRef, useState } from 'react'

import type { Art } from '../lib/auszeichnung'
import { alsText, auswahlIn, lese, zeichneAus } from '../lib/reichtext'

/**
 * A writing field that can carry markup -- and every place it is read (1.69.0).
 *
 * **Why this exists.** Since 1.69.0 there are two such fields: the composer in
 * the chat and the sheet for changing a post. From use: „im Übrigen sollte das
 * auch im Änderungsmodus funktionieren." Both need the same four steps, and
 * they are easy to get subtly wrong:
 *
 * - after every input the field is read back into text (`lese()`), because the
 *   field is the truth and the state follows it -- never the other way round,
 *   that would put the cursor at the end;
 * - pasted content is taken **as text only** (`alsText()`), otherwise foreign
 *   HTML lands in the chat;
 * - a mark is put on and the field read again -- a mark that nobody reads back
 *   is lost on sending;
 * - and whether something is selected at all, because the buttons only make
 *   sense then.
 *
 * Written out twice, these four run apart -- the house has practised that
 * three times (`grabPoster()`, `Emoji`, the tab „Bearbeiten"). So they stand
 * here once, and each caller keeps only what makes it different: the composer
 * grows with its text, sends on Enter and carries a placeholder; the sheet
 * fills its height and lets Enter make a paragraph.
 *
 * @param onGelesen What the field now says, as text with characters.
 */
export function useSchreibfeld(onGelesen: (text: string) => void) {
  const feld = useRef<HTMLDivElement>(null)
  const [hatAuswahl, setHatAuswahl] = useState(false)

  /*
   * **Die Frage „ist etwas markiert?" hängt am Dokument, nicht am Feld.**
   * `selectionchange` gibt es nur dort; `auswahlIn()` entscheidet danach, ob
   * die Auswahl in *diesem* Feld liegt. Zwei Felder auf einer Seite hören
   * beide zu und beantworten die Frage jedes für sich.
   */
  useEffect(() => {
    const schauen = () => setHatAuswahl(auswahlIn(feld.current))
    document.addEventListener('selectionchange', schauen)
    return () => document.removeEventListener('selectionchange', schauen)
  }, [])

  function lies(): void {
    if (feld.current !== null) onGelesen(lese(feld.current))
  }

  return {
    feld,
    hatAuswahl,

    /**
     * Eine Auszeichnung auf die Auswahl legen -- oder wieder wegnehmen.
     *
     * **Die Auswahl bleibt stehen.** Deshalb hängt der Knopf an `mousedown`
     * mit `preventDefault` (in `Auszeichnungsknoepfe`): ein Knopf, der den
     * Fokus nimmt, löscht die Markierung, und danach gibt es nichts mehr
     * auszuzeichnen.
     */
    lege(art: Exclude<Art, 'text'>): void {
      if (feld.current === null) return
      zeichneAus(art)
      lies()
    },

    onInput: lies,

    onPaste(event: React.ClipboardEvent): void {
      // Aus einer fremden Seite kommt fremdes HTML. Hier zählt der Wortlaut,
      // und sonst nichts.
      event.preventDefault()
      alsText(event.clipboardData.getData('text/plain'))
      lies()
    },
  }
}
